import styled from '@emotion/styled';
import HeartFill from '@icons/favorite/HeartFill';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { memo } from 'react';

import { MapProps } from '../types';
import useSupercluster from './hooks/useSupercluster';

function ClusteredMarkers({ markers, onMarkerClick }: Pick<MapProps, 'markers' | 'onMarkerClick'>) {
    const { clusters } = useSupercluster(markers);

    return (
        <>
            {clusters.map((cluster) => {
                const [lng, lat] = cluster.geometry.coordinates;
                const position = { lat, lng };

                if (cluster.properties.cluster) {
                    const { point_count: count } = cluster.properties;
                    return (
                        <AdvancedMarker key={`cluster-${cluster.id}`} position={position}>
                            <ClusterIcon count={count}>{count}</ClusterIcon>
                        </AdvancedMarker>
                    );
                }

                const {
                    id, name, isMyShop,
                } = cluster.properties;
                return (
                    <AdvancedMarker
                        key={id}
                        position={position}>
                        <MarkerIcon onClick={() => onMarkerClick?.(id, lat, lng)}>
                            {isMyShop && <HeartFill />}
                            {name}
                        </MarkerIcon>
                    </AdvancedMarker>
                );
            })}
        </>
    );
}

function getClusterSize(count: number): number {
    if (count < 10) return 40;
    if (count < 50) return 55;
    return 74;
}

const ClusterIcon = styled.div<{ count: number }>`
    align-items: center;
    background: var(--color-white);
    border-radius: 50%;
    color: var(--color-text-secondary);
    cursor: pointer;
    display: flex;
    font-size: var(--font-size-body-long);
    font-weight: bold;
    height: ${({ count }) => getClusterSize(count)}px;
    justify-content: center;
    width: ${({ count }) => getClusterSize(count)}px;
`;

const MarkerIcon = styled.div`
    align-items: center;
    background-color: var(--color-white);
    border-radius: var(--radius-full);
    color: var(--color-text-secondary);
    column-gap: var(--spacing-1);
    display: flex;
    font-size: var(--font-size-body-long);
    font-weight: bold;
    height: 32px;
    line-height: 1.4;
    padding: var(--spacing-1) var(--spacing-2);
    white-space: nowrap;
`;

export default memo(ClusteredMarkers, (prev, next) => {
    if (prev.markers === next.markers) return true;
    if (prev.markers?.length !== next.markers?.length) return false;
    return prev.markers!.every((m, i) => {
        const n = next.markers![i];
        return m.id === n.id && m.lat === n.lat && m.lng === n.lng && m.name === n.name && m.isMyShop === n.isMyShop;
    });
});
